import {Link, useNavigate} from 'react-router-dom';
import {LucideIcon, formatNumber} from '@tryghost/shade/utils';
import {Spinner} from '@/components/ui/Spinner';
import {useArchivePlan, useDuplicatePlan, usePlanStats, usePlans, useUpdatePlan} from '@/hooks/usePlans';
import {useMediaQuery} from '@/hooks/useMediaQuery';
import {useState} from 'react';
import type {PlanStats, PlatformPlan} from '@/types/api';

function formatCents(cents: number): string {
    if (cents === 0) {
        return '—';
    }
    return `$${formatNumber(Math.round(cents / 100))}`;
}

function PlanBadge({text, featured}: { text?: string | null; featured?: boolean }) {
    if (!text && !featured) {
        return null;
    }
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '3px 10px',
                borderRadius: 40,
                fontSize: '1.0rem',
                fontWeight: 600,
                letterSpacing: '0.3px',
                textTransform: 'uppercase',
                background: featured ? 'var(--studio-tint)' : 'var(--tt-badge-bg)',
                color: featured ? 'var(--studio-accent)' : 'var(--tt-text-secondary)',
                marginLeft: 8
            }}
        >
            {text || 'Featured'}
        </span>
    );
}

function StatusIndicator({active}: { active: boolean }) {
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6
            }}
        >
            <span
                style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: active ? 'var(--tt-text-secondary)' : 'var(--tt-text-tertiary)',
                    boxShadow: active ? '0 0 8px var(--studio-soft)' : 'none'
                }}
            />
            <span
                style={{
                    fontSize: '1.2rem',
                    fontWeight: 500,
                    color: active ? 'var(--tt-text-secondary)' : 'var(--tt-text-secondary)'
                }}
            >
                {active ? 'Active' : 'Archived'}
            </span>
        </span>
    );
}

function ConfirmDialog({
    title,
    message,
    confirmLabel,
    confirmColor,
    onConfirm,
    onCancel,
    isLoading
}: {
    title: string;
    message: string;
    confirmLabel: string;
    confirmColor?: string;
    onConfirm: () => void;
    onCancel: () => void;
    isLoading: boolean;
}) {
    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--tt-modal-backdrop)'
            }}
            onClick={onCancel}
        >
            <div className="studio-panel"
                style={{background: 'var(--tt-card-bg)', borderRadius: 14, padding: 32, maxWidth: 440, width: '90%'}}
                onClick={e => e.stopPropagation()}
            >
                <h3
                    style={{fontSize: '1.8rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: '0 0 8px 0', letterSpacing: '-.025em'}}
                >
                    {title}
                </h3>
                <p
                    style={{
                        fontSize: '1.4rem',
                        color: 'var(--tt-text-secondary)',
                        margin: '0 0 24px 0',
                        lineHeight: 1.5
                    }}
                >
                    {message}
                </p>
                <div style={{display: 'flex', justifyContent: 'flex-end', gap: 12}}>
                    <button className="studio-control" disabled={isLoading}
                        style={{height: 40, padding: '0 20px', borderRadius: 8, background: 'transparent', border: '1px solid var(--tt-card-border)', color: 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s ease'}}
                        type="button"
                        onClick={onCancel}
                    >
                        Cancel
                    </button>
                    <button className="studio-control studio-control-primary" disabled={isLoading}
                        style={{background: confirmColor || 'var(--tt-accent)', cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.7 : 1, fontFamily: 'inherit'}}
                        type="button"
                        onClick={onConfirm}
                    >
                        {isLoading ? 'Processing...' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

function DuplicateDialog({
    planName,
    onConfirm,
    onCancel,
    isLoading
}: {
    planName: string;
    onConfirm: (newSlug: string) => void;
    onCancel: () => void;
    isLoading: boolean;
}) {
    const [newSlug, setNewSlug] = useState('');

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--tt-modal-backdrop)'
            }}
            onClick={onCancel}
        >
            <div className="studio-panel"
                style={{background: 'var(--tt-card-bg)', borderRadius: 14, padding: 32, maxWidth: 440, width: '90%'}}
                onClick={e => e.stopPropagation()}
            >
                <h3
                    style={{fontSize: '1.8rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: '0 0 8px 0', letterSpacing: '-.025em'}}
                >
                    Duplicate Plan
                </h3>
                <p
                    style={{
                        fontSize: '1.4rem',
                        color: 'var(--tt-text-secondary)',
                        margin: '0 0 20px 0',
                        lineHeight: 1.5
                    }}
                >
                    Create a copy of &quot;{planName}&quot; with a new slug.
                </p>
                <div style={{marginBottom: 24}}>
                    <label
                        style={{
                            display: 'block',
                            fontSize: '1.2rem',
                            fontWeight: 600,
                            color: 'var(--tt-text-secondary)',
                            marginBottom: 8,
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px'
                        }}
                    >
                        New Slug
                    </label>
                    <input className="studio-field-control"
                        placeholder="e.g., pro-annual"
                        style={{width: '100%', height: 44, padding: '0 14px', borderRadius: 8, border: '1px solid var(--tt-input-border)', fontSize: '13px', color: 'var(--tt-text-primary)', background: 'var(--tt-input-bg)', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s ease'}}
                        type="text"
                        value={newSlug}
                        onBlur={(e) => {
                            e.currentTarget.style.borderColor = 'var(--tt-card-border)';
                        }}
                        onChange={e => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                        onFocus={(e) => {
                            e.currentTarget.style.borderColor = 'var(--tt-accent)';
                        }}
                    />
                </div>
                <div style={{display: 'flex', justifyContent: 'flex-end', gap: 12}}>
                    <button className="studio-control" disabled={isLoading}
                        style={{height: 40, padding: '0 20px', borderRadius: 8, background: 'transparent', border: '1px solid var(--tt-card-border)', color: 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit'}}
                        type="button"
                        onClick={onCancel}
                    >
                        Cancel
                    </button>
                    <button className="studio-control studio-control-primary" disabled={isLoading || !newSlug}
                        style={{cursor: isLoading || !newSlug ? 'not-allowed' : 'pointer', opacity: isLoading || !newSlug ? 0.5 : 1, fontFamily: 'inherit'}}
                        type="button"
                        onClick={() => onConfirm(newSlug)}
                    >
                        {isLoading ? 'Creating...' : 'Duplicate'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export function PlansListPage() {
    const navigate = useNavigate();
    const {isMobile, isTablet} = useMediaQuery();
    const isCompact = isMobile || isTablet;
    const [showArchived, setShowArchived] = useState(false);
    const [confirmAction, setConfirmAction] = useState<{
        planId: string;
        planName: string;
        action: 'archive' | 'unarchive';
    } | null>(null);
    const [duplicateAction, setDuplicateAction] = useState<{
        planId: string;
        planName: string;
    } | null>(null);

    const {data: plans, isLoading} = usePlans();
    const {data: stats} = usePlanStats();
    const archiveMutation = useArchivePlan();
    const updateMutation = useUpdatePlan();
    const duplicateMutation = useDuplicatePlan();

    // Count archived plans to determine if we should show the "View Archive" button
    const archivedCount = (plans || []).filter(p => !p.is_active).length;
    const activeCount = (plans || []).filter(p => p.is_active).length;
    const featuredCount = (plans || []).filter(p => p.is_active && p.is_featured).length;
    const subscribedSites = (stats || []).reduce((total, stat) => total + stat.site_count, 0);

    // Show ONLY archived plans when showArchived is true, ONLY active plans when false
    const filteredPlans = showArchived
        ? (plans || []).filter(p => !p.is_active)
        : (plans || []).filter(p => p.is_active);

    const getStatForPlan = (planId: string): PlanStats | undefined => {
        return stats?.find(s => s.id === planId);
    };

    const handleArchive = async () => {
        if (!confirmAction) {
            return;
        }
        try {
            if (confirmAction.action === 'archive') {
                await archiveMutation.mutateAsync(confirmAction.planId);
            } else {
                await updateMutation.mutateAsync({
                    id: confirmAction.planId,
                    data: {is_active: Boolean(true)}
                });
            }
            setConfirmAction(null);
        } catch {
            // Keep the dialog open on error so user can see what happened
        }
    };

    const handleDuplicate = async (newSlug: string) => {
        if (!duplicateAction) {
            return;
        }
        await duplicateMutation.mutateAsync({id: duplicateAction.planId, newSlug});
        setDuplicateAction(null);
    };

    if (isLoading) {
        return (
            <div className="studio-content-page" style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, maxWidth: 'none', margin: 0, fontFamily: 'inherit'}}>
                <Spinner size={80} />
            </div>
        );
    }

    return (
        <div className="studio-content-page studio-admin-page studio-admin-plans-page">
            {/* Header */}
            <header className="studio-page-heading studio-admin-page-heading"
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: isCompact ? 'flex-start' : 'center',
                    flexDirection: isCompact ? 'column' : 'row',
                    gap: isCompact ? 16 : 0,
                    marginBottom: isCompact ? 24 : 32
                }}
            >
                <div>
                    <p className="studio-eyebrow">Commercial configuration</p>
                    <h1>
                        {showArchived ? 'Archived Plans' : 'Platform Plans'}
                    </h1>
                    <p className="studio-admin-intro">
                        {showArchived
                            ? 'Plans that have been archived and are no longer available'
                            : 'Configure subscription tiers and their limits'
                        }
                    </p>
                </div>

                <div className="studio-admin-heading-actions">
                    {/* Only show archive button if there are archived plans OR we're currently viewing archive */}
                    {(archivedCount > 0 || showArchived) && (
                        <button className="studio-control studio-control-primary" style={{display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: 'inherit', background: showArchived ? 'var(--tt-accent)' : 'var(--tt-bg)', border: showArchived ? 'none' : '1px solid var(--tt-card-border)', color: showArchived ? '#fff' : 'var(--tt-text-primary)'}}
                            type="button"
                            onClick={() => setShowArchived(!showArchived)}
                            onMouseEnter={(e) => {
                                if (!showArchived) {
                                    e.currentTarget.style.borderColor = 'var(--tt-accent)';
                                    e.currentTarget.style.color = 'var(--tt-accent)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!showArchived) {
                                    e.currentTarget.style.borderColor = 'var(--tt-card-border)';
                                    e.currentTarget.style.color = 'var(--tt-text-primary)';
                                }
                            }}
                        >
                            {showArchived ? (
                                <>
                                    <svg fill="none" height="16" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16">
                                        <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    Back to Active
                                </>
                            ) : (
                                <>
                                    <svg fill="none" height="16" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16">
                                        <polyline points="21,8 21,21 3,21 3,8" />
                                        <rect height="5" width="22" x="1" y="3" />
                                        <line x1="10" x2="14" y1="12" y2="12" />
                                    </svg>
                                    View Archive
                                </>
                            )}
                        </button>
                    )}
                    {!showArchived && (
                        <Link className="studio-control studio-control-primary"
                            style={{display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none'}}
                            to="/superadmin/plans/new"
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 90, 43, 0.35)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 90, 43, 0.25)';
                            }}
                        >
                            <svg fill="none" height="16" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" width="16">
                                <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                            </svg>
                            Create Plan
                        </Link>
                    )}
                </div>
            </header>

            <section aria-label="Plan overview" className="studio-admin-overview-grid studio-admin-plan-overview">
                <article><div><span>Active plans</span><strong>{formatNumber(activeCount)}</strong><p>Available for subscriptions</p></div><i><LucideIcon.Crown size={21} /></i></article>
                <article><div><span>Featured plans</span><strong>{formatNumber(featuredCount)}</strong><p>Highlighted in pricing</p></div><i><LucideIcon.BadgeCheck size={21} /></i></article>
                <article><div><span>Subscribed publications</span><strong>{formatNumber(subscribedSites)}</strong><p>Publications assigned to plans</p></div><i><LucideIcon.Newspaper size={21} /></i></article>
            </section>

            {/* Plans Grid */}
            <div className="studio-admin-plan-grid"
                style={{
                    display: 'grid',
                    gridTemplateColumns: isCompact ? '1fr' : 'repeat(auto-fill, minmax(360px, 1fr))',
                    gap: 20
                }}
            >
                {filteredPlans.map((plan: PlatformPlan) => {
                    const stat = getStatForPlan(plan.id);
                    return (
                        <div key={plan.id}
                            className="studio-panel"
                            style={{background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', borderRadius: 14, overflow: 'hidden', transition: 'all 0.2s ease', opacity: plan.is_active ? 1 : 0.7}}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--tt-accent)';
                                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.08)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--tt-card-border)';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            {/* Plan Header */}
                            <div style={{padding: '24px 24px 0'}}>
                                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12}}>
                                    <div style={{display: 'flex', alignItems: 'center'}}>
                                        <h3
                                            style={{fontSize: '1.8rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: 0, letterSpacing: '-.025em'}}
                                        >
                                            {plan.name}
                                        </h3>
                                        {(plan.badge_text || plan.is_featured) && (
                                            <PlanBadge featured={plan.is_featured} text={plan.badge_text} />
                                        )}
                                    </div>
                                    <StatusIndicator active={plan.is_active} />
                                </div>
                                <p
                                    style={{
                                        fontSize: '1.3rem',
                                        color: 'var(--tt-text-tertiary)',
                                        margin: '0 0 4px 0',
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    {plan.slug}
                                </p>
                                {plan.description && (
                                    <p
                                        style={{
                                            fontSize: '1.3rem',
                                            color: 'var(--tt-text-secondary)',
                                            margin: '8px 0 0 0',
                                            lineHeight: 1.5
                                        }}
                                    >
                                        {plan.description}
                                    </p>
                                )}
                            </div>

                            {/* Pricing */}
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr 1fr',
                                    gap: 1,
                                    margin: '20px 0',
                                    background: 'var(--tt-divider)',
                                    borderTop: '1px solid var(--tt-divider)',
                                    borderBottom: '1px solid var(--tt-divider)'
                                }}
                            >
                                {[
                                    {label: 'Monthly', value: plan.price_monthly_cents},
                                    {label: 'Yearly', value: plan.price_yearly_cents},
                                    {label: 'Lifetime', value: plan.price_lifetime_cents}
                                ].map(price => (
                                    <div
                                        key={price.label}
                                        style={{
                                            padding: '16px 12px',
                                            textAlign: 'center',
                                            background: 'var(--tt-card-bg)'
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: '1.8rem',
                                                fontWeight: 700,
                                                color: price.value > 0 ? 'var(--tt-text-primary)' : 'var(--tt-text-tertiary)'
                                            }}
                                        >
                                            {formatCents(price.value)}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: '1.1rem',
                                                color: 'var(--tt-text-secondary)',
                                                marginTop: 4,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.3px',
                                                fontWeight: 500
                                            }}
                                        >
                                            {price.label}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Stats & Actions */}
                            <div style={{padding: '0 24px 24px'}}>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: 16
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8
                                        }}
                                    >
                                        <svg fill="none" height="16" stroke="var(--tt-text-tertiary)" strokeWidth="2" viewBox="0 0 24 24" width="16">
                                            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" />
                                            <path d="M2 12H22" />
                                            <path d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" />
                                        </svg>
                                        <span style={{fontSize: '1.3rem', color: 'var(--tt-text-secondary)'}}>
                                            <strong style={{color: 'var(--tt-text-primary)'}}>{formatNumber(stat?.site_count || 0)}</strong> publications
                                        </span>
                                    </div>
                                    <div style={{fontSize: '1.2rem', color: 'var(--tt-text-tertiary)'}}>
                                        Order: {formatNumber(plan.sort_order)}
                                    </div>
                                </div>

                                <div style={{display: 'flex', gap: 8}}>
                                    <button className="studio-control" style={{flex: 1, height: 38, borderRadius: 8, background: 'var(--tt-bg)', border: '1px solid var(--tt-card-border)', color: 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s ease'}}
                                        type="button"
                                        onClick={() => navigate(`/superadmin/plans/${plan.id}`)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--tt-accent)';
                                            e.currentTarget.style.color = 'var(--tt-accent)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--tt-card-border)';
                                            e.currentTarget.style.color = 'var(--tt-text-primary)';
                                        }}
                                    >
                                        Edit
                                    </button>
                                    <button className="studio-control" style={{width: 38, height: 38, borderRadius: 8, background: 'var(--tt-bg)', border: '1px solid var(--tt-card-border)', color: 'var(--tt-text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s ease', fontWeight: 500, fontSize: '13px', fontFamily: 'inherit'}}
                                        title="Duplicate"
                                        type="button"
                                        onClick={() => setDuplicateAction({planId: plan.id, planName: plan.name})}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--tt-accent)';
                                            e.currentTarget.style.color = 'var(--tt-accent)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--tt-card-border)';
                                            e.currentTarget.style.color = 'var(--tt-text-secondary)';
                                        }}
                                    >
                                        <svg fill="none" height="16" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16">
                                            <rect height="13" rx="2" width="13" x="9" y="9" />
                                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                                        </svg>
                                    </button>
                                    {plan.is_active ? (
                                        <button className="studio-control" style={{width: 38, height: 38, borderRadius: 8, background: 'var(--tt-bg)', border: '1px solid var(--tt-card-border)', color: 'var(--tt-text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s ease', fontWeight: 500, fontSize: '13px', fontFamily: 'inherit'}}
                                            title="Archive"
                                            type="button"
                                            onClick={() => setConfirmAction({planId: plan.id, planName: plan.name, action: 'archive'})}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.borderColor = 'var(--tt-red)';
                                                e.currentTarget.style.color = 'var(--tt-red)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.borderColor = 'var(--tt-card-border)';
                                                e.currentTarget.style.color = 'var(--tt-text-secondary)';
                                            }}
                                        >
                                            <svg fill="none" height="16" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16">
                                                <polyline points="21,8 21,21 3,21 3,8" />
                                                <rect height="5" width="22" x="1" y="3" />
                                                <line x1="10" x2="14" y1="12" y2="12" />
                                            </svg>
                                        </button>
                                    ) : (
                                        <button className="studio-control" style={{width: 38, height: 38, borderRadius: 8, background: 'var(--tt-bg)', border: '1px solid var(--tt-card-border)', color: 'var(--tt-text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s ease', fontWeight: 500, fontSize: '13px', fontFamily: 'inherit'}}
                                            title="Restore"
                                            type="button"
                                            onClick={() => setConfirmAction({planId: plan.id, planName: plan.name, action: 'unarchive'})}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.borderColor = 'var(--tt-text-secondary)';
                                                e.currentTarget.style.color = 'var(--tt-text-secondary)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.borderColor = 'var(--tt-card-border)';
                                                e.currentTarget.style.color = 'var(--tt-text-secondary)';
                                            }}
                                        >
                                            <svg fill="none" height="16" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16">
                                                <polyline points="23 4 23 10 17 10" />
                                                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {filteredPlans.length === 0 && (
                <div className="studio-panel"
                    style={{textAlign: 'center', padding: '64px 24px', background: 'var(--tt-card-bg)', borderRadius: 14, border: '1px solid var(--tt-card-border)'}}
                >
                    <div
                        style={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            background: 'var(--tt-badge-bg)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px'
                        }}
                    >
                        <svg fill="none" height="28" stroke="var(--tt-text-tertiary)" strokeWidth="2" viewBox="0 0 24 24" width="28">
                            <rect height="16" rx="2" width="18" x="3" y="4" />
                            <path d="M3 10h18" />
                            <path d="M7 15h4" />
                        </svg>
                    </div>
                    <h3
                        style={{fontSize: '1.6rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: '0 0 8px 0', letterSpacing: '-.025em'}}
                    >
                        {showArchived ? 'No archived plans' : 'No active plans'}
                    </h3>
                    <p
                        style={{
                            fontSize: '1.4rem',
                            color: 'var(--tt-text-secondary)',
                            margin: 0
                        }}
                    >
                        {showArchived
                            ? 'You don\'t have any archived plans. Archive plans to hide them from the active list.'
                            : 'All plans are archived. Click "View Archive" to see them.'
                        }
                    </p>
                </div>
            )}

            {confirmAction && (
                <ConfirmDialog
                    confirmColor={confirmAction.action === 'archive' ? 'var(--tt-red)' : 'var(--tt-text-secondary)'}
                    confirmLabel={confirmAction.action === 'archive' ? 'Archive' : 'Restore'}
                    isLoading={archiveMutation.isLoading || updateMutation.isLoading}
                    message={
                        confirmAction.action === 'archive'
                            ? `Are you sure you want to archive "${confirmAction.planName}"? It will no longer be available for new subscriptions.`
                            : `Are you sure you want to restore "${confirmAction.planName}"? It will become available for new subscriptions.`
                    }
                    title={confirmAction.action === 'archive' ? 'Archive Plan' : 'Restore Plan'}
                    onCancel={() => setConfirmAction(null)}
                    onConfirm={handleArchive}
                />
            )}

            {duplicateAction && (
                <DuplicateDialog
                    isLoading={duplicateMutation.isLoading}
                    planName={duplicateAction.planName}
                    onCancel={() => setDuplicateAction(null)}
                    onConfirm={handleDuplicate}
                />
            )}
        </div>
    );
}
